/**
 * Presentation logic for the assistant panel.
 *
 * Split out of the .tsx on purpose: `vitest.config.ts` only collects `.ts`,
 * so anything with a decision in it lives here where it can be tested. The
 * component is left as markup plus wiring.
 */
import type { IntakeMessage, IntakeStatus, IntakeView, RequestDraft } from '../../lib/api'

// ─── Transcript ──────────────────────────────────────────────────

/**
 * What the reporter actually sees.
 *
 * `tool` rows are the model's own scratchpad — they read like
 * "Saved. Still missing: priority. Ask the user about those." Showing them
 * would leak prompt-shaped instructions into a support conversation, so the
 * tool activity is surfaced as draft state (the chips + checklist) instead.
 * `system` rows are never persisted, but filtering them is free insurance.
 */
export function visibleMessages(messages: IntakeMessage[] | undefined): IntakeMessage[] {
  if (!messages) return []
  return messages.filter(
    (m) => (m.role === 'user' || m.role === 'assistant') && m.content.trim() !== '',
  )
}

// ─── Draft fields ────────────────────────────────────────────────

/**
 * Human labels for the `missing` field names the API returns. Phrased as
 * things the reporter still has to tell us, not as schema keys — "steps to
 * reproduce" rather than `stepsToReproduce`.
 */
export const FIELD_LABEL: Record<string, string> = {
  kind: 'what kind of request this is',
  title: 'a short title',
  summary: 'a description',
  area: 'which part of the app',
  priority: 'how urgent it is',
  stepsToReproduce: 'steps to reproduce',
  expectedBehavior: 'what you expected to happen',
  actualBehavior: 'what actually happened',
  impact: 'who or what this blocks',
  sourceUrl: 'the page this happened on',
}

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field
}

/** "a short title, how urgent it is, and steps to reproduce" */
export function describeMissing(missing: string[]): string {
  const labels = missing.map(fieldLabel)
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]!
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

export interface DraftChip {
  key: string
  label: string
  tone: 'neutral' | 'accent' | 'warning' | 'danger'
}

const PRIORITY_TONE: Record<string, DraftChip['tone']> = {
  urgent: 'danger',
  high: 'warning',
  med: 'neutral',
  low: 'neutral',
}

const KIND_LABEL: Record<string, string> = {
  feature: 'Feature request',
  bug: 'Bug',
  question: 'Question',
  chore: 'Chore',
}

/**
 * The at-a-glance summary of what the agent has understood so far. Only
 * populated fields become chips — an empty draft renders nothing rather than a
 * row of "—" placeholders.
 */
export function draftChips(draft: RequestDraft): DraftChip[] {
  const chips: DraftChip[] = []
  if (draft.kind) {
    chips.push({ key: 'kind', label: KIND_LABEL[draft.kind] ?? draft.kind, tone: 'accent' })
  }
  if (draft.area) chips.push({ key: 'area', label: draft.area, tone: 'neutral' })
  if (draft.priority) {
    chips.push({
      key: 'priority',
      label: draft.priority === 'med' ? 'medium' : draft.priority,
      tone: PRIORITY_TONE[draft.priority] ?? 'neutral',
    })
  }
  return chips
}

// ─── Status ──────────────────────────────────────────────────────

export interface StatusPresentation {
  label: string
  tone: 'neutral' | 'accent' | 'success' | 'warning' | 'danger'
  /** Shown under the header when there's something the reporter should know. */
  note: string | null
  /** Whether the composer accepts input. */
  canSend: boolean
  /** Whether to offer the explicit "File it" action. */
  canSubmit: boolean
  /** Whether the conversation is over and only "Start another" makes sense. */
  isClosed: boolean
}

export function statusPresentation(view: IntakeView | null): StatusPresentation {
  if (!view) {
    return {
      label: 'New',
      tone: 'neutral',
      note: null,
      canSend: true,
      canSubmit: false,
      isClosed: false,
    }
  }

  const status: IntakeStatus = view.status

  switch (status) {
    case 'submitted':
      return {
        label: 'Filed',
        tone: 'success',
        // The agent may have run with a no-op tracker, in which case there is
        // genuinely no ticket. Say so rather than showing a blank key.
        note: view.issue
          ? null
          : 'Recorded and the team was notified, but the issue tracker is not connected yet — there is no ticket number.',
        canSend: false,
        canSubmit: false,
        isClosed: true,
      }
    case 'discarded':
      return {
        label: 'Discarded',
        tone: 'neutral',
        note: 'This request was discarded.',
        canSend: false,
        canSubmit: false,
        isClosed: true,
      }
    case 'needs_human':
      return {
        label: 'Escalated',
        tone: 'warning',
        note:
          view.escalationReason
            ? `Passed to a person: ${view.escalationReason}`
            : 'Passed to a person — someone will pick this up.',
        canSend: true,
        canSubmit: view.submittable,
        isClosed: false,
      }
    case 'ready':
      return {
        label: 'Ready to file',
        tone: 'accent',
        note: null,
        canSend: true,
        canSubmit: true,
        isClosed: false,
      }
    case 'gathering':
    default:
      return {
        label: 'Collecting details',
        tone: 'neutral',
        note: null,
        canSend: true,
        canSubmit: false,
        isClosed: false,
      }
  }
}

// ─── Errors ──────────────────────────────────────────────────────

/**
 * Turns an API error into something worth showing a colleague. The proxy
 * answers 503 when the CHAT_AGENT service binding is absent (local dev, or an
 * environment where the agent Worker isn't deployed) and 403 when a read_only
 * role tries to write — both are expected states, not crashes, and neither
 * should read as "something went wrong".
 */
export function describeError(err: unknown): { message: string; recoverable: boolean } {
  const status = (err as { status?: number } | undefined)?.status
  const message = (err as { message?: string } | undefined)?.message

  if (status === 503) {
    return {
      message: 'The assistant is not available in this environment yet.',
      recoverable: false,
    }
  }
  if (status === 403) {
    return {
      message: 'Your role is read-only, so you cannot file requests.',
      recoverable: false,
    }
  }
  if (status === 401) {
    return { message: 'Your session expired. Reload the page and sign in again.', recoverable: false }
  }
  return {
    message: message || 'Something went wrong. Try sending that again.',
    recoverable: true,
  }
}

// ─── Time ────────────────────────────────────────────────────────

/** Compact relative time for message stamps. `now` is injectable for tests. */
export function formatTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  const mins = Math.floor((now.getTime() - d.getTime()) / 60000)
  if (!Number.isFinite(mins)) return ''
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Session persistence ─────────────────────────────────────────

const STORAGE_KEY = 'studio-os:assistant:request-id'

/**
 * The open conversation survives closing the panel and navigating between
 * routes, but not a new tab or a browser restart — sessionStorage, not local.
 * An intake is a single sitting; resuming a three-day-old draft would be more
 * confusing than starting fresh.
 *
 * Every accessor is guarded: sessionStorage throws in Safari private mode and
 * is absent under SSR, and neither is worth breaking the panel over.
 */
export function readStoredRequestId(storage: Storage | undefined = safeStorage()): string | null {
  try {
    const value = storage?.getItem(STORAGE_KEY) ?? null
    // Only accept ids of the shape the agent Worker mints — a stale or hand-
    // edited value would otherwise 400 on every request.
    return value && /^ir_[0-9a-f]{12}$/.test(value) ? value : null
  } catch {
    return null
  }
}

export function writeStoredRequestId(
  id: string | null,
  storage: Storage | undefined = safeStorage(),
): void {
  try {
    if (id) storage?.setItem(STORAGE_KEY, id)
    else storage?.removeItem(STORAGE_KEY)
  } catch {
    /* private mode — the panel just won't resume */
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  } catch {
    return undefined
  }
}
